import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import axios from '../api/axiosConfig';
import { useAuth } from '../context/AuthContext';

export default function OAuthCallback() {
    const navigate  = useNavigate();
    const { checkAuth } = useAuth();
    const [error, setError] = useState('');
    const hasRun = useRef(false);

    useEffect(() => {
        if (hasRun.current) return;
        hasRun.current = true;
        // ── PKCE flow: code in query string ───────────────────────────────────
        const searchParams = new URLSearchParams(window.location.search);
        const code         = searchParams.get('code');
        const codeVerifier = sessionStorage.getItem('pkce_verifier');

        if (code && codeVerifier) {
            sessionStorage.removeItem('pkce_verifier');
            window.history.replaceState(null, '', window.location.pathname);
            axios.post('/api/app_user/oauth-signin', { code, codeVerifier })
                .then(() => checkAuth())
                .then(() => navigate('/'))
                .catch(() => {
                    setError('Sign-in failed. Please try again.');
                    setTimeout(() => navigate('/login'), 3000);
                });
            return;
        }

        // ── Implicit flow: access_token in hash ────────────────────────────────
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const errorCode  = hashParams.get('error') || searchParams.get('error');
        const errorDesc  = hashParams.get('error_description') || searchParams.get('error_description');

        if (errorCode) {
            setError(errorDesc || 'Google sign-in failed. Please try again.');
            setTimeout(() => navigate('/login'), 3000);
            return;
        }

        const accessToken  = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');

        if (accessToken) {
            window.history.replaceState(null, '', window.location.pathname);
            axios.post('/api/app_user/oauth-signin', { accessToken, refreshToken })
                .then(() => checkAuth())
                .then(() => navigate('/'))
                .catch(() => {
                    setError('Sign-in failed. Please try again.');
                    setTimeout(() => navigate('/login'), 3000);
                });
            return;
        }

        setError('No token received. Please try again.');
        setTimeout(() => navigate('/login'), 3000);
    }, [navigate, checkAuth]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                   justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
            {error ? (
                <Alert severity="error" sx={{ maxWidth: 400 }}>{error}</Alert>
            ) : (
                <>
                    <CircularProgress />
                    <Typography color="text.secondary">Signing you in…</Typography>
                </>
            )}
        </Box>
    );
}
